/**
 * Reads an unknown value (a file the user may well have hand-edited) into a
 * ChronoDocument, or reports precisely what is wrong with it.
 *
 * Validation lives here in TypeScript rather than in a published JSON Schema:
 * no dependency, and far better messages on the day a hand-edited file fails to
 * open (implementation-plan.md §7).
 */

import type {
  Band,
  BandUnit,
  Category,
  ChronoDocument,
  EventItem,
  Item,
  PeriodItem,
} from './types';

export const SCHEMA_VERSION = 1;

export type MigrateResult =
  | { ok: true; doc: ChronoDocument }
  | { ok: false; errors: string[] };

const BAND_UNITS: readonly BandUnit[] = ['year', 'yearBP', 'MaBP'];
const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

class Errors {
  readonly list: string[] = [];

  add(path: string, message: string): void {
    this.list.push(`${path} : ${message}`);
  }

  get ok(): boolean {
    return this.list.length === 0;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(value: unknown, path: string, errors: Errors): string {
  if (typeof value !== 'string' || value.trim() === '') {
    errors.add(path, 'chaîne de caractères non vide attendue.');
    return '';
  }
  return value;
}

function readNumber(value: unknown, path: string, errors: Errors): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    errors.add(path, `nombre fini attendu (reçu ${JSON.stringify(value)}).`);
    return 0;
  }
  return value;
}

/**
 * A bound is a plain year. The old `{ year, fuzzy }` form is still read so files
 * written before approximate bounds were dropped keep opening; the flag is
 * discarded, which is exactly what removing the feature means.
 */
function readBound(value: unknown, path: string, errors: Errors): number {
  if (typeof value === 'number') return readNumber(value, path, errors);
  if (isRecord(value)) return readNumber(value['year'], `${path}.year`, errors);

  errors.add(path, 'année (nombre) attendue.');
  return 0;
}

function readLane(value: unknown, path: string, errors: Errors): number | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
    errors.add(path, 'entier positif ou nul attendu.');
    return undefined;
  }
  return value;
}

function readCategory(value: unknown, path: string, errors: Errors): Category {
  if (!isRecord(value)) {
    errors.add(path, 'objet { name, color } attendu.');
    return { name: '', color: '#cccccc' };
  }
  const color = readString(value['color'], `${path}.color`, errors);
  if (color !== '' && !HEX_COLOR.test(color)) {
    errors.add(`${path}.color`, `couleur hexadécimale attendue (reçu "${color}").`);
  }
  return { name: readString(value['name'], `${path}.name`, errors), color };
}

function readBand(value: unknown, path: string, errors: Errors): Band {
  if (!isRecord(value)) {
    errors.add(path, 'objet attendu.');
    return { label: '', start: 0, end: 0, step: 1 };
  }

  const band: Band = {
    label: readString(value['label'], `${path}.label`, errors),
    start: readNumber(value['start'], `${path}.start`, errors),
    end: readNumber(value['end'], `${path}.end`, errors),
    step: readNumber(value['step'], `${path}.step`, errors),
  };

  const unit = value['unit'];
  if (unit !== undefined) {
    if (typeof unit !== 'string' || !BAND_UNITS.includes(unit as BandUnit)) {
      errors.add(`${path}.unit`, `unité inconnue (attendu : ${BAND_UNITS.join(', ')}).`);
    } else {
      band.unit = unit as BandUnit;
    }
  }

  if (value['pageBreakBefore'] === true) band.pageBreakBefore = true;

  return band;
}

function readItem(value: unknown, path: string, errors: Errors): Item {
  const fallback: EventItem = { type: 'event', cat: '', label: '', year: 0 };

  if (!isRecord(value)) {
    errors.add(path, 'objet attendu.');
    return fallback;
  }

  const type = value['type'];
  if (type !== 'period' && type !== 'event') {
    errors.add(`${path}.type`, `"period" ou "event" attendu (reçu ${JSON.stringify(type)}).`);
    return fallback;
  }

  const cat = readString(value['cat'], `${path}.cat`, errors);
  const label = readString(value['label'], `${path}.label`, errors);
  const lane = readLane(value['lane'], `${path}.lane`, errors);

  if (type === 'period') {
    const period: PeriodItem = {
      type: 'period',
      cat,
      label,
      start: readBound(value['start'], `${path}.start`, errors),
      end: readBound(value['end'], `${path}.end`, errors),
    };
    if (lane !== undefined) period.lane = lane;
    return period;
  }

  const event: EventItem = {
    type: 'event',
    cat,
    label,
    year: readNumber(value['year'], `${path}.year`, errors),
  };
  if (lane !== undefined) event.lane = lane;

  const span = value['span'];
  if (span !== undefined) {
    if (!Array.isArray(span) || span.length !== 2) {
      errors.add(`${path}.span`, 'paire [début, fin] attendue.');
    } else {
      event.span = [
        readNumber(span[0], `${path}.span[0]`, errors),
        readNumber(span[1], `${path}.span[1]`, errors),
      ];
    }
  }

  return event;
}

export function migrate(raw: unknown): MigrateResult {
  const errors = new Errors();

  if (!isRecord(raw)) {
    return { ok: false, errors: ['Le fichier ne contient pas un document Chrono-Arrow.'] };
  }

  const version = raw['schemaVersion'];
  if (typeof version !== 'number' || !Number.isInteger(version) || version < 1) {
    return { ok: false, errors: ['schemaVersion : entier ≥ 1 attendu. Ce fichier n\'est pas un document Chrono-Arrow.'] };
  }
  if (version > SCHEMA_VERSION) {
    return {
      ok: false,
      errors: [
        `Ce document a été créé par une version plus récente de Chrono-Arrow ` +
          `(schemaVersion ${version}, cette version en gère ${SCHEMA_VERSION}).`,
      ],
    };
  }

  // No migration step yet: v1 is the first schema. Future versions chain here,
  // upgrading `raw` one version at a time before it is read below.

  const categories: Record<string, Category> = {};
  const rawCategories = raw['categories'];
  if (!isRecord(rawCategories)) {
    errors.add('categories', 'objet attendu.');
  } else {
    for (const [id, value] of Object.entries(rawCategories)) {
      categories[id] = readCategory(value, `categories.${id}`, errors);
    }
  }

  const bands: Band[] = [];
  const rawBands = raw['bands'];
  if (!Array.isArray(rawBands)) {
    errors.add('bands', 'tableau attendu.');
  } else {
    rawBands.forEach((value, i) => bands.push(readBand(value, `bands[${i}]`, errors)));
  }

  const items: Item[] = [];
  const rawItems = raw['items'];
  if (!Array.isArray(rawItems)) {
    errors.add('items', 'tableau attendu.');
  } else {
    rawItems.forEach((value, i) => items.push(readItem(value, `items[${i}]`, errors)));
  }

  const rawMeta = isRecord(raw['meta']) ? raw['meta'] : {};
  const savedAt = typeof rawMeta['savedAt'] === 'string' ? rawMeta['savedAt'] : new Date().toISOString();
  const appVersion = typeof rawMeta['appVersion'] === 'string' ? rawMeta['appVersion'] : 'inconnue';

  if (!errors.ok) return { ok: false, errors: errors.list };

  return {
    ok: true,
    doc: { schemaVersion: SCHEMA_VERSION, meta: { savedAt, appVersion }, categories, bands, items },
  };
}
